/** Action items - what is waiting on the signed-in person (ADR-0212 decision 6c; card C-H, ADR-0067).
 *
 * The bell's destination and a place of the menu. Three tabs live at this address - **Waiting on
 * you**, **Submitted by me** and **Decided** (`?tab=submitted`, `?tab=decided`) - and a fourth,
 * **Notifications**, is its own address (`/notifications`) that lights the same place. The strip of
 * tabs is the shell's (`routeTable.ts`); this screen only switches the page by the address.
 *
 * Each tab is a table page: a title, three tiles, one filter line and one table. The explanations
 * that used to stand over the queue - how a decision is made, what the three answers are, who has a
 * list at all - are behind info controls (`infoText.ts`, section `actions`) and nothing was deleted.
 *
 * **Four properties, each asserted by a test named after what it protects.**
 *
 * - **Empty and failed are different renderings** (KEEP.md 19). *"Nothing is waiting on you."* is a
 *   claim that a person's queue is clear; a dropped connection must not be able to say it.
 * - **The queue is not assembled here.** One server-filtered read behind `useWaitingOnMe`, the same
 *   one the bell and Home use, so a count in the bell is the count under it.
 * - **A role with no list is told so, and asks for nothing.** Submitted by me exists for the roles
 *   that submit, Decided for the roles that decide; any other role reads one sentence and makes no
 *   request. Not an empty list (which reads as *nothing is owed*), not a disabled anything.
 * - **Submitter separation is not re-derived.** `decide` refuses it; the row does not guess.
 */

import type { ReactNode } from "react";

import { maySubmit, mayHaveQueue } from "../api/approvals";
import { useMe } from "../api/auth";
import { TablePage } from "../components/ui";
import { ListChecks } from "@phosphor-icons/react";
import { RequireSession } from "../shell/RequireSession";
import { DecidedPage, SubmittedPage } from "./ApprovalsLists";
import { WaitingPage } from "./ApprovalsWaiting";
import { ScreenFrame } from "./ScreenFrame";
import { useScreen } from "./useScreen";
import { useUrlTab } from "./useUrlTab";

const TABS = [
  { key: "waiting", label: "Waiting on you" },
  { key: "submitted", label: "Submitted by me" },
  { key: "decided", label: "Decided" },
] as const;

/** A role that has no list for the tab: one sentence saying who has one. */
function NoList({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <TablePage
      icon={ListChecks}
      title="Action items"
      label={label}
      state={
        <p data-testid={testId}>{children}</p>
      }
    />
  );
}

function ApprovalsBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const role = me?.role;
  const [tab] = useUrlTab(TABS);

  let body: ReactNode;
  if (tab === "submitted") {
    body =
      maySubmit(role) || role === undefined ? (
        <SubmittedPage enabled={maySubmit(role)} />
      ) : (
        <NoList label="Submitted by me" testId="approvals-no-submitted">
          Only an Admin, an Approver or a Contributor sends a record for approval.
        </NoList>
      );
  } else if (tab === "decided") {
    body =
      mayHaveQueue(role) || role === undefined ? (
        <DecidedPage enabled={mayHaveQueue(role)} />
      ) : (
        <NoList label="Decided" testId="approvals-no-decided">
          Only an Admin or an Approver sees what has been decided.
        </NoList>
      );
  } else {
    // The Auditor has no queue here; their read-only overview of what is closed, asked and filed is
    // the first tab of Approvals & audit (`Engagement.tsx`), where they land after signing in.
    body = <WaitingPage screen={screen} role={role} />;
  }

  return (
    <ScreenFrame screen={screen} title="Action items">
      {body}
    </ScreenFrame>
  );
}

export function ApprovalsScreen() {
  return (
    <RequireSession>
      <ApprovalsBody />
    </RequireSession>
  );
}
